import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import './TenantDropdown.css';

const TenantDropdown = ({ onTenantChange, selectedTenant }) => {
  const { authenticatedFetch, setAvailableTenants } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [keyboardBuffer, setKeyboardBuffer] = useState('');
  const [lastKeyTime, setLastKeyTime] = useState(0);

  const fetchTenants = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/tenants`);

      if (response.ok) {
        const data = await response.json();
        // Filter out the "template" tenant
        const filteredTenants = data.tenants.filter(tenant => tenant.name.toLowerCase() !== 'template');
        setTenants(filteredTenants);
        setAvailableTenants(filteredTenants);
        // Set first tenant as default if none selected
        if (filteredTenants.length > 0 && !selectedTenant) {
          onTenantChange(filteredTenants[0]);
        }
      } else {
        console.error('Failed to fetch tenants');
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, setAvailableTenants, selectedTenant, onTenantChange]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const handleTenantSelect = (tenant) => {
    onTenantChange(tenant);
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        return;
      }
    }

    if (isOpen) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsOpen(false);
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const currentIndex = tenants.findIndex(t => t.id === selectedTenant?.id);
        const nextIndex = currentIndex < tenants.length - 1 ? currentIndex + 1 : 0;
        onTenantChange(tenants[nextIndex]);
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const currentIndex = tenants.findIndex(t => t.id === selectedTenant?.id);
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : tenants.length - 1;
        onTenantChange(tenants[prevIndex]);
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(false);
        return;
      }

      // Handle backspace to clear buffer
      if (e.key === 'Backspace') {
        e.preventDefault();
        setKeyboardBuffer(prev => prev.slice(0, -1));
        return;
      }

      // Handle keyboard search
      if (e.key.length === 1 && /[a-zA-Z0-9]/.test(e.key)) {
        e.preventDefault();
        const now = Date.now();
        const timeDiff = now - lastKeyTime;
        
        let newBuffer;
        // If more than 1 second has passed, reset buffer
        if (timeDiff > 1000) {
          newBuffer = e.key.toLowerCase();
        } else {
          newBuffer = keyboardBuffer + e.key.toLowerCase();
        }
        
        setKeyboardBuffer(newBuffer);
        setLastKeyTime(now);
        
        // Find tenant that starts with the new buffer
        const matchingTenant = tenants.find(tenant => 
          tenant.name.toLowerCase().startsWith(newBuffer)
        );
        
        if (matchingTenant) {
          onTenantChange(matchingTenant);
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="tenant-dropdown">
        <div className="tenant-selector loading">
          <span className="tenant-icon">🏢</span>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (tenants.length === 0) {
    return (
      <div className="tenant-dropdown">
        <div className="tenant-selector disabled">
          <span className="tenant-icon">🏢</span>
          <span>No tenants</span>
        </div>
      </div>
    );
  }

  return (
    <div className="tenant-dropdown">
      <div 
        className={`tenant-selector ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls="tenant-listbox"
        aria-haspopup="listbox"
      >
        <span className="tenant-icon">🏢</span>
        <span className="tenant-name">
          {selectedTenant ? selectedTenant.name : 'Select Tenant'}
        </span>
        <span className="dropdown-arrow">▼</span>
      </div>
      
      {isOpen && (
        <div className="tenant-options" role="listbox">
          {tenants.map((tenant) => (
            <div
              key={tenant.id}
              className={`tenant-option ${selectedTenant?.id === tenant.id ? 'selected' : ''}`}
              onClick={() => handleTenantSelect(tenant)}
              role="option"
              aria-selected={selectedTenant?.id === tenant.id}
            >
              <span className="tenant-icon">🏢</span>
              <span className="tenant-name">{tenant.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TenantDropdown;











